import coolCSS from "./NavBar.module.css";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import StudyCasesData from "../../data/StudycasesData";
import { toast, ToastContainer, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './toastStyles.css'; // Notification custom styles here
import ThemeToggle from "./ThemeToggle";
import { Clock, Coffee, Microphone } from 'phosphor-react';

const toastDefaults = {
    position: "top-center",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "dark",
    transition: Bounce,
    className: "custom-toast",
}

export const toastify = {
    warn: (message) => toast.warn(message, toastDefaults),
    info: (message) => toast.info(message, toastDefaults),
    error: (message) => toast.error(message, toastDefaults),
    success: (message) => toast.success(message, toastDefaults),
};

function NavBar() {
    const Life = {
        dayH1: "Day in my Life",
        moments: [
            { time: "6:30", icon: "📖", text: "Study and Learn" },
            { time: "8:00", icon: "💻", text: "Design work in Figma" },
            { time: "14:30", icon: "🏊‍♂️", text: "Swim Practice" },
            { time: "17:00", icon: "📞", text: "Meeting with clients" },
            { time: "19:00", icon: "🌅", text: "Hang out with friends, music & chill" },
        ],
    };



    const [open, setOpen] = useState(false);
    const [listening, setListening] = useState(false);
    const lang = "en-US";

    const location = useLocation();
    const navigate = useNavigate();
    const recognitionRef = useRef(null);

    //TODO: Edit keywords to match something cooler
    const sections = useMemo(() => {
        const staticSections = [
            { id: "hero", keywords: ["hero", "start", "welcome"], route: "/" },
            { id: "works", keywords: ["work", "projects"], route: "/" },
            { id: "experiments", keywords: ["experiment", "test", "demo"], route: "/" },
            { id: "about", keywords: ["about", "profile", "me"], route: "/" },
            { id: "footer", keywords: ["footer", "contact", "links"], route: "/" },
            { id: "resume", keywords: ["resume", "cv", "curriculum"], route: "/resume" },
            { id: "rl-resume", keywords: ["real resume", "real cv", "life curriculum"], route: "/real-life-resume" },
        ];

        const caseStudySections = Object.entries(StudyCasesData).map(([slug, data]) => ({
            id: slug,
            keywords: [
                data?.hero?.h1?.toLowerCase?.() || "",
                slug,
                ...(data.voiceKeywords || []),
            ],
            route: `/${slug}`,
        }));

        return [...staticSections, ...caseStudySections];
    }, []);

    const fuse = useMemo(() => new Fuse(sections, {
        keys: ["keywords"],
        threshold: 0.4,
    }), [sections]);


    const speak = useCallback((text) => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = lang;
        window.speechSynthesis.speak(utterance);
    }, []);

    const handleCommand = useCallback((text) => {
        const result = fuse.search(text);
        if (result.length > 0) {
            const matched = result[0].item;
            speak(`Navigating to ${matched.id.replace(/-/g, " ")}`);
            toastify.success(`Navigating to ${matched.id.replace(/-/g, " ")}`);

            if (location.pathname === matched.route) {
                const el = document.getElementById(matched.id);
                if (el) el.scrollIntoView({ behavior: "smooth" });
                else {
                    toastify.info(`You're already on ${matched.route}`);
                }
            } else {
                navigate(matched.route);
            }
        } else {
            speak("Sorry, I didn't catch that.");
            toastify.warn("Sorry, I didn't catch that.");
        }
    }, [fuse, location.pathname, navigate, speak]);


    useEffect(() => {
        if ("webkitSpeechRecognition" in window) {
            const recognition = new window.webkitSpeechRecognition();
            recognition.lang = lang;
            recognition.continuous = false;
            recognition.interimResults = false;

            recognition.onresult = (event) => {
                const text = event.results[0][0].transcript.toLowerCase().trim();
                setListening(false);
                handleCommand(text);
            };

            recognition.onend = () => setListening(false);

            recognition.onerror = (e) => {
                setListening(false);
                if (e.error === "not-allowed") {
                    toastify.warn("Please allow microphone access to use voice navigation.");
                } else {
                    toastify.error("Voice recognition error: " + e.error);
                }
            };

            recognitionRef.current = recognition;
        } //else {
            //toastify.error("Sorry, your browser does not support Speech Recognition.");
        //}
    }, [lang, handleCommand]);

    const startListening = () => {
        if (!recognitionRef.current) {
            toastify.error("Sorry, your browser does not support Speech Recognition.");
            return;
        }
        setListening(true);
        recognitionRef.current.start();
    };

    return (
        <main id={coolCSS.navbar}>
            {/* Daily Timeline Modal */}
            <div className={`${coolCSS.coolContainer} ${coolCSS.timeline}`}>
                <button className={coolCSS.coolButton} onClick={() => setOpen(true)}>
                    <Clock weight="fill" className={coolCSS.icons}/>{Life?.dayH1}
                </button>

                {open && (
                    <div className={coolCSS.modalOverlay} onClick={() => setOpen(false)}>
                        <div className={coolCSS.modalContent} onClick={(e) => e.stopPropagation()}>
                            <h2 className={coolCSS.modalTitle}>{Life?.dayH1}</h2>
                            <ul className={coolCSS.modalTimeline}>
                                {Life?.moments.map((item, i) => (
                                    <li key={i}>
                                        <strong>{item.icon} {item.time}</strong>
                                        <p>{item.text}</p>
                                    </li>
                                ))}
                            </ul>
                            <button className={coolCSS.closeButton} onClick={() => setOpen(false)}>Close</button>
                        </div>
                    </div>
                )}
            </div>

            {/* <a href="/blog" target="_blank" rel="noreferrer" className={`${coolCSS.coolButton} ${coolCSS.voiceNav}`}>
            <Scroll weight="fill" className={coolCSS.icons}/> My Journal
            </a> */}

            {/* Clickable Cards Hint */}
            {/* <div className={coolCSS.clickHint}>
                <span>^^ HEY, those work cards are clickable!</span>
            </div> */}

            {/* Voice Nav Buttons */}
            <div className={`${coolCSS.coolContainer} ${coolCSS.voiceNav}`}>
                <div className={coolCSS.popoverWrapper}>
                    <button
                        className={`${coolCSS.coolButton} ${listening ? coolCSS.listening : ""}`}
                        onClick={startListening}
                    >
                        <Microphone weight="fill" className={coolCSS.icons}/>{listening ? "Listening..." : "Voice Nav"}
                    </button>

                    {listening && (
                        <div className={coolCSS.popover}>
                            Try saying <span>"about"</span>
                        </div>
                    )}
                </div>
            </div>
            
            <ThemeToggle />

            <div className={coolCSS.mobileItems}>
                <div className={coolCSS.coolContainer}>
                    <button className={coolCSS.coolButton} onClick={() => setOpen(true)}>
                        <Clock weight="fill" className={coolCSS.icons}/>{Life?.dayH1}
                    </button>

                    {open && (
                        <div className={coolCSS.modalOverlay} onClick={() => setOpen(false)}>
                            <div className={coolCSS.modalContent} onClick={(e) => e.stopPropagation()}>
                                <h2 className={coolCSS.modalTitle}>{Life?.dayH1}</h2>
                                <ul className={coolCSS.modalTimeline}>
                                    {Life?.moments.map((item, i) => (
                                        <li key={i}>
                                            <strong>{item.icon} {item.time}</strong>
                                            <p>{item.text}</p>
                                        </li>
                                    ))}
                                </ul>
                                <button className={coolCSS.closeButton} onClick={() => setOpen(false)}>Close</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Voice Nav Buttons */}
                <div className={coolCSS.coolContainer}>
                    <div className={coolCSS.popoverWrapper}>
                        <button
                            className={`${coolCSS.coolButton} ${listening ? coolCSS.listening : ""}`}
                            onClick={startListening}
                        >
                            <Microphone weight="fill" className={coolCSS.icons}/>{listening ? "Listening..." : "Voice Nav"}
                        </button>

                        {listening && (
                            <div className={coolCSS.popover}>
                                Try saying <span>"about"</span>
                            </div>
                        )}
                    </div>
                </div>

                <ThemeToggle />

                <a href="https://www.buymeacoffee.com/kianboncilli" target="_blank" rel="noopener noreferrer">
                    <button class={coolCSS.coolButton}><Coffee weight="fill" className={coolCSS.icons}/> Buy me a coffee</button>
                </a>
            </div>


            <ToastContainer
                position="top-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
            />
        </main>
    );
}

export default NavBar;
